import has from 'lodash/fp/has';

const DEFAULT_LOCALE = 'en-GB';

interface LocaleMap {
    [key: string]: string;
}

const BRAZIL_TENANT_PREFIX = 'rio-brazil';
const KOREA_TENANT_PREFIX = 'rio-southkorea';
const SOUTH_AFRICA_TENANT_PREFIX = 'rio-southafrica';
const EUROPE_TENANT_PREFIX = 'rio-eu';

const countryToLocaleMap: LocaleMap = {
    at: 'de-DE',
    be: 'nl-NL',
    br: 'pt-BR',
    bg: 'bg-BG',
    ch: 'de-DE',
    cy: 'el-GR',
    cz: 'cs-CZ',
    dk: 'da-DK',
    de: 'de-DE',
    es: 'es-ES',
    ee: 'et-EE',
    fi: 'fi-FI',
    fr: 'fr-FR',
    gb: 'en-GB',
    gr: 'el-GR',
    hr: 'hr-HR',
    hu: 'hu-HU',
    ie: 'en-GB',
    is: 'nb-NO',
    it: 'it-IT',
    ko: 'ko-KR',
    li: 'de-DE',
    lt: 'lt-LT',
    lu: 'fr-FR',
    lv: 'lv-LV',
    mt: 'en-GB',
    nl: 'nl-NL',
    no: 'nb-NO',
    pl: 'pl-PL',
    pt: 'pt-PT',
    ro: 'ro-RO',
    se: 'sv-SE',
    si: 'sl-SI',
    sk: 'sk-SK',
    sl: 'sl-SI',
    sv: 'sv-SE',
};

const supportedLocaleMap: LocaleMap = {
    bg: 'bg-BG',
    'bg-BG': 'bg-BG',
    cs: 'cs-CZ',
    'cs-CZ': 'cs-CZ',
    da: 'da-DK',
    'da-DK': 'da-DK',
    de: 'de-DE',
    'de-DE': 'de-DE',
    el: 'el-GR',
    'el-GR': 'el-GR',
    en: 'en-GB',
    'en-GB': 'en-GB',
    es: 'es-ES',
    'es-ES': 'es-ES',
    et: 'et-EE',
    'et-EE': 'et-EE',
    fi: 'fi-FI',
    'fi-FI': 'fi-FI',
    fr: 'fr-FR',
    'fr-FR': 'fr-FR',
    hr: 'hr-HR',
    'hr-HR': 'hr-HR',
    hu: 'hu-HU',
    'hu-HU': 'hu-HU',
    it: 'it-IT',
    'it-IT': 'it-IT',
    ko: 'ko-KR',
    'ko-KR': 'ko-KR',
    lt: 'lt-LT',
    'lt-LT': 'lt-LT',
    lv: 'lv-LV',
    'lv-LV': 'lv-LV',
    nb: 'nb-NO',
    'nb-NO': 'nb-NO',
    nl: 'nl-NL',
    'nl-NL': 'nl-NL',
    no: 'nb-NO',
    pl: 'pl-PL',
    'pl-PL': 'pl-PL',
    pt: 'pt-PT',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-PT',
    ro: 'ro-RO',
    'ro-RO': 'ro-RO',
    sk: 'sk-SK',
    'sk-SK': 'sk-SK',
    sl: 'sl-SI',
    'sl-SI': 'sl-SI',
    sv: 'sv-SE',
    'sv-SE': 'sv-SE',
};

const SUPPORTED_LOCALES_DEFAULT = ['en-GB'];
const SUPPORTED_LOCALES_EUROPE = [
    'bg-BG',
    'cs-CZ',
    'da-DK',
    'de-DE',
    'en-GB',
    'es-ES',
    'el-GR',
    'et-EE',
    'fi-FI',
    'fr-FR',
    'hr-HR',
    'hu-HU',
    'it-IT',
    'lt-LT',
    'lv-LV',
    'nb-NO',
    'nl-NL',
    'pl-PL',
    'pt-PT',
    'ro-RO',
    'sk-SK',
    'sl-SI',
    'sv-SE',
].sort();
const SUPPORTED_LOCALES_BRAZIL = ['pt-BR', 'es-ES', 'en-GB'].sort();
const SUPPORTED_LOCALES_SOUTHAFRICA = ['en-GB'].sort();
const SUPPORTED_LOCALES_SOUTHKOREA = ['ko-KR', 'en-GB'].sort();

export const getSupportedLocaleForTenant = (tenant: string) => {
    if (tenant.startsWith(EUROPE_TENANT_PREFIX)) {
        return SUPPORTED_LOCALES_EUROPE;
    } else if (tenant.startsWith(BRAZIL_TENANT_PREFIX)) {
        return SUPPORTED_LOCALES_BRAZIL;
    } else if (tenant.startsWith(KOREA_TENANT_PREFIX)) {
        return SUPPORTED_LOCALES_SOUTHKOREA;
    } else if (tenant.startsWith(SOUTH_AFRICA_TENANT_PREFIX)) {
        return SUPPORTED_LOCALES_SOUTHAFRICA;
    } else {
        return SUPPORTED_LOCALES_DEFAULT;
    }
};

const extractLanguage = (locale?: string | null): string => (locale ?? DEFAULT_LOCALE).split('-')[0];

const DEFAULT_LANG = extractLanguage(DEFAULT_LOCALE);

const getSupportedLocale = (preferredLocale: string): string =>
    has(preferredLocale, supportedLocaleMap) ? supportedLocaleMap[preferredLocale] : DEFAULT_LOCALE;

export { DEFAULT_LANG, DEFAULT_LOCALE, extractLanguage, supportedLocaleMap, countryToLocaleMap, getSupportedLocale };
